import { useState, useEffect, useCallback, useRef } from 'react';
import { api, DfeInventoryEntry, DfeInventoryListResult } from '@/lib/api';

interface UseInventoryOptions {
  search?: string;
  fungibility?: string;
  sortBy?: string;
  sortOrder?: string;
}

interface UseInventoryReturn {
  items: DfeInventoryEntry[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  loadMore: () => void;
  hasMore: boolean;
}

export function useInventory(options: UseInventoryOptions = {}): UseInventoryReturn {
  const [items, setItems] = useState<DfeInventoryEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const limit = 20;
  const fetchIdRef = useRef(0);

  const fetchInventory = useCallback(
    async (newOffset: number = 0, append: boolean = false) => {
      const id = ++fetchIdRef.current;
      try {
        setLoading(true);
        const result: DfeInventoryListResult = await api.getDfeInventory({
          search: options.search || undefined,
          fungibility: options.fungibility || undefined,
          sortBy: options.sortBy || 'name',
          sortOrder: options.sortOrder || 'asc',
          limit,
          offset: newOffset,
        });
        if (fetchIdRef.current !== id) return;
        if (append) {
          setItems((prev) => [...prev, ...result.items]);
        } else {
          setItems(result.items);
        }
        setTotal(result.total);
        setOffset(newOffset);
      } catch (err) {
        if (fetchIdRef.current !== id) return;
        setError(err instanceof Error ? err.message : 'Failed to load inventory');
      } finally {
        if (fetchIdRef.current === id) {
          setLoading(false);
        }
      }
    },
    [options.search, options.fungibility, options.sortBy, options.sortOrder],
  );

  const refresh = useCallback(() => {
    setError(null);
    fetchInventory(0, false);
  }, [fetchInventory]);

  const loadMore = useCallback(() => {
    if (!loading && offset + limit < total) {
      fetchInventory(offset + limit, true);
    }
  }, [fetchInventory, loading, offset, limit, total]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return {
    items,
    total,
    loading,
    error,
    refresh,
    loadMore,
    hasMore: offset + limit < total,
  };
}
